import { SupabaseClient } from "@supabase/supabase-js";
import { BackgroundTask, BackgroundTaskStatus, TaskParameters, TaskType } from "@/app/types/tasks";
import WorkFactory from "../workFactory";

/**
 * Insert a new background task into the queue.
 *
 * @param client - Supabase client
 * @param type - Task type enum
 * @param initialStatus - Starting status for the task
 * @param parameters - Task parameters to persist
 * @returns Created task object
 */
export async function addBackgroundTask(
  client: SupabaseClient,
  type: TaskType,
  initialStatus: BackgroundTaskStatus = "notstarted",
  parameters: TaskParameters
): Promise<BackgroundTask | null> {
  const { data, error } = await client
    .from("background_tasks")
    .insert({
      type,
      status: initialStatus,
      parameters,
      retry_count: 0,
      next_retry_at: null,
    })
    .select("*")
    .single();

  if (error) {
    throw new Error(`Error adding background task: ${error.message}`);
  }

  return WorkFactory.taskToJSON(data);
}

/**
 * Update the status or result of an existing background task.
 *
 * @param client - Supabase client
 * @param id - Task identifier
 * @param status - New status
 * @param result - Optional result payload
 * @param error - Optional error message
 * @param retry_count - Optional retry counter
 * @param next_retry_at - Optional next retry timestamp
 * @returns Updated task object
 */
export async function updateBackgroundTask(
  client: SupabaseClient,
  id: string,
  status: BackgroundTaskStatus,
  result: string = "",
  error: string = "",
  retry_count?: number,
  next_retry_at?: Date | null,
  metadata?: Record<string, any>
): Promise<BackgroundTask | null> {
  const updateData: any = {
    status,
    result,
    error,
    updated_at: new Date().toISOString(),
  };

  if (typeof retry_count !== "undefined") {
    updateData.retry_count = retry_count;
  }
  if (typeof next_retry_at !== "undefined") {
    updateData.next_retry_at = next_retry_at?.toISOString() || null;
  }
  if (typeof metadata !== "undefined") {
    updateData.metadata = metadata;
  }

  const { data, error: updateError } = await client
    .from("background_tasks")
    .update(updateData)
    .eq("id", id)
    .select("*")
    .single();

  if (updateError) {
    throw new Error(`Error updating background task: ${updateError.message}`);
  }

  return WorkFactory.taskToJSON(data);
}

/**
 * Fetch the oldest background task that has not started.
 *
 * @param client - Supabase client
 * @returns The next task to process or null when none are pending
 */
export async function getNextPendingTask(client: SupabaseClient): Promise<BackgroundTask | null> {
  const { data: tasks, error } = await client
    .from("background_tasks")
    .select("*")
    .eq("status", "notstarted")
    .order("created_at", { ascending: true })
    .limit(1);

  if (error) {
    console.error("Error getting next pending task:", error);
    return null;
  }

  if (!tasks || tasks.length === 0) {
    return null;
  }

  return WorkFactory.taskToJSON(tasks[0]);
}

/**
 * Retrieve a background task by id.
 *
 * @param client - Supabase client
 * @param id - Task identifier
 * @returns Task record or null
 */
export async function getBackgroundTask(
  client: SupabaseClient,
  id: string
): Promise<BackgroundTask | null> {
  const { data, error } = await client
    .from("background_tasks")
    .select("*")
    .eq("id", id)
    .maybeSingle();

  if (error) {
    throw new Error(`Error updating background task: ${error.message}`);
  }

  return WorkFactory.taskToJSON(data);
}

/**
 * Fetch the most recent workflow run and its child tasks for a work.
 *
 * @param client - Supabase client
 * @param workId - UUID of the work
 * @returns Object containing parent and children tasks or null
 */
export async function getLatestWorkflowRun(
  client: SupabaseClient,
  workId: string
): Promise<{ parent: BackgroundTask; children: BackgroundTask[] } | null> {
  const { data: parentData, error } = await client
    .from("background_tasks")
    .select("*")
    .eq("type", "workflow")
    .is("parent_id", null)
    .eq("parameters->>workId", workId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    throw error;
  }

  if (!parentData) {
    return null;
  }

  const parent = WorkFactory.taskToJSON(parentData);

  const { data: childData, error: childError } = await client
    .from("background_tasks")
    .select("*")
    .eq("parent_id", parent.id)
    .order("created_at", { ascending: true });

  if (childError) {
    throw childError;
  }

  const children = (childData || []).map((c) => WorkFactory.taskToJSON(c));

  return { parent, children };
}
